import type React from "react";
import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { Badge, Table } from "@xplatform/design-system";
import { IconSparkles, IconStarFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ReviewStatus, type RowEdit } from "@models/job";
import {
  ConfidenceBand,
  type MappingResultRow,
  type MappingResultRowOe,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import { useResultRow } from "@hooks/jobResults/useResultRow";
import RevisionHistoryDrawer from "@components/RevisionHistoryDrawer/RevisionHistoryDrawer";
import { PLACEHOLDER, resolveDisplayTarget } from "@utils/rowHelpers";
import RowStatusCell from "../RowStatusCell";
import RowActionsCell from "../RowActionsCell";
import RowFindingBadge from "../RowFindingBadge";

interface WdJobResultsTableRowProps {
  row: MappingResultRowOe;
  edit: RowEdit | undefined;
  saveState: RowSaveStatus | undefined;
  jobId: string;
  registerRef: (el: HTMLTableRowElement | null) => void;
  onAccept: () => void;
  onReject: () => void;
  onRecover: () => void;
  onRetrySave: () => void;
  onRowRestored: (row: MappingResultRow) => void;
}

/**
 * One OE row: the part as the supplier named it, and the catalogue component
 * it resolved to.
 *
 * A catalogue MISS is the case this row is shaped around. The backend leaves
 * BOTH catalogue fields empty on one and puts the model's proposal in
 * `suggestedText` — so the id column stays blank (that emptiness IS the
 * finding) and the text column shows the proposal, marked as a proposal.
 * `isProposal` is what decides that, never the row's `mappingStatus`: the
 * latter is the pipeline's verdict and never changes, so a row the reviewer
 * has since corrected onto a real entry would keep being flagged.
 */
const WdJobResultsTableRow: React.FC<WdJobResultsTableRowProps> = ({
  row,
  edit,
  saveState,
  jobId,
  registerRef,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
  onRowRestored,
}) => {
  const { t } = useTranslation();
  const {
    historyOpen,
    setHistoryOpen,
    effectiveStatus,
    isFailed,
    isSaving,
    actionsDisabled,
  } = useResultRow(row, edit, saveState);

  const { source } = row;
  // No candidate list: wd shows the one suggestion the pipeline settled on.
  // The alternatives panel is gone, so there is nothing else to resolve
  // against — a stored `chosen_rank` falls back to the row's own target.
  const target = resolveDisplayTarget(row, edit, undefined);
  const isProposal = target?.isProposal ?? false;
  // A proposal is not always the model's. When the naming step returns nothing
  // the backend falls back to the part's own OE name so the row is still
  // actionable — showing that under an "AI Suggestion" badge would credit the
  // model with the customer's own words.
  const isAiSuggestion = target?.isAiSuggestion ?? false;
  // A row the model CHOSE, and how sure it was. Nothing marked these before:
  // an alias hit is pre-accepted and a miss carries a proposal, so the rows the
  // select call answered were the only ones arriving at Pending with no mark on
  // them at all — indistinguishable, at a glance, from a row nothing had run on.
  const aiConfidence = target?.aiConfidence ?? null;

  const dimmed = (
    <Text size="xs" c="dimmed">
      {PLACEHOLDER}
    </Text>
  );

  return (
    <>
      <Table.Tr
        ref={registerRef}
        className="job-results-page__row"
        tabIndex={0}
        data-row-index={row.rowIndex}
      >
        <Table.Td>
          <Text size="sm" c="dimmed" ff="monospace" fw={500}>
            {row.rowIndex + 1}
          </Text>
        </Table.Td>

        {/* Source · OE Number */}
        <Table.Td>
          {source.oeNumber ? (
            <Text size="sm" ff="monospace">
              {source.oeNumber}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Name */}
        <Table.Td>
          {isFailed ? (
            <Text size="xs" c="red" lineClamp={2}>
              {row.error?.message ?? t("resultRow.status.failed")}
            </Text>
          ) : source.oeName ? (
            <Text size="sm" lineClamp={3}>
              {source.oeName}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Function */}
        <Table.Td>
          {source.oeFunction ? (
            <Text size="sm" lineClamp={2}>
              {source.oeFunction}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Domain */}
        <Table.Td>
          {source.oeDomain ? (
            <Text size="sm" lineClamp={2}>
              {source.oeDomain}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Suggested · ItemMP ID — blank on a proposal, which has no
            catalogue row to point at. */}
        <Table.Td>
          {target?.itemMpId != null ? (
            <Text size="sm" ff="monospace" ta="left">
              {target.itemMpId}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Suggested · ItemMP Text — or the model's proposal, said to be one.
            Under it, what the pipeline concluded about this suggestion.

            The finding sits HERE, not in the Status column, because it judges
            this cell: "Require Review" and "Exact Match" are both claims about
            the entry named above them, and a verdict two cells away from the
            thing it judges is one the reviewer has to go looking for. It
            renders a size down from the Status badge — the name is the
            content, the finding is a footnote to it.

            The AI marker no longer REPLACES the finding. They answer different
            questions — "the catalogue has no entry for this part" is about the
            ROW and is true whether or not anything was proposed, while "AI
            suggested" is a caveat on the NAME beside it. Tying them together
            meant a miss with no proposal (the model omitted the row, or its
            reply failed to align) rendered as an empty cell with no badge at
            all, reading as a row nothing had looked at yet. */}
        <Table.Td>
          {isProposal || target?.itemMpText ? (
            <Stack gap={2} align="flex-start">
              {isProposal ? (
                <Tooltip
                  multiline
                  w={280}
                  label={
                    isAiSuggestion
                      ? t("jobResultsPage.wdProposedTooltip", {
                          defaultValue:
                            "Not in the database. This is the name AI suggests the entry should have — a proposal, not a stored record.",
                        })
                      : t("jobResultsPage.wdUnnamedTooltip", {
                          defaultValue:
                            "Not in the database, and AI proposed no name for it. This is the part's own OE text, shown so the entry can still be requested.",
                        })
                  }
                >
                  <Text size="sm" ta="left" lineClamp={2} fs="italic">
                    {target?.suggestedText}
                  </Text>
                </Tooltip>
              ) : (
                <Text size="sm" ta="left" lineClamp={2}>
                  {target?.itemMpText}
                </Text>
              )}
              <Group gap={4} wrap="wrap">
                {isAiSuggestion && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="violet"
                    leftSection={<IconStarFilled size={10} />}
                  >
                    {t("jobResultsPage.aiSuggested", {
                      defaultValue: "AI Suggestion",
                    })}
                  </Badge>
                )}
                {aiConfidence && (
                  <Tooltip
                    multiline
                    w={280}
                    label={t("jobResultsPage.wdAiMappedTooltip", {
                      defaultValue:
                        "Chosen by AI from the catalogue, with the confidence it reported. Not a confirmed mapping — nobody has accepted it yet.",
                    })}
                  >
                    <Badge
                      size="xs"
                      variant="light"
                      color={aiConfidence === ConfidenceBand.High ? "blue" : "yellow"}
                      leftSection={<IconSparkles size={10} />}
                    >
                      {t(`jobResultsPage.aiMapped.${aiConfidence}`, {
                        defaultValue:
                          aiConfidence === ConfidenceBand.High
                            ? "AI Mapped · high"
                            : "AI Mapped · medium",
                      })}
                    </Badge>
                  </Tooltip>
                )}
                <RowFindingBadge row={row} target={target} />
              </Group>
            </Stack>
          ) : (
            /* No name to show — but the finding still has to be said, or a
               catalogue miss with no proposal is a blank cell. */
            <Stack gap={2} align="flex-start">
              {dimmed}
              <RowFindingBadge row={row} target={target} />
            </Stack>
          )}
        </Table.Td>

        {/* Status — the REVIEWER axis only. The pipeline's finding renders
            under the suggestion it is about; see the ItemMP Text cell above. */}
        <RowStatusCell
          status={effectiveStatus}
          saveState={saveState}
          onRetrySave={onRetrySave}
        />

        <RowActionsCell
          isSaving={isSaving}
          actionsDisabled={actionsDisabled}
          hasTopMatch={target !== null}
          isRejected={effectiveStatus === ReviewStatus.Rejected}
          onAccept={onAccept}
          onReject={onReject}
          onOpenHistory={() => setHistoryOpen(true)}
          onRecover={onRecover}
        />
      </Table.Tr>

      <RevisionHistoryDrawer
        opened={historyOpen}
        jobId={jobId}
        rowIndex={row.rowIndex}
        onRestored={onRowRestored}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

export default WdJobResultsTableRow;
